import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';
import { normalizeDiscount, priceTier } from '@theme/offer-pricing';

/**
 * Offer cards and option rows ("tops" = one unit row) rendered by snippets/offer-tiers.liquid.
 *
 * The cards are a radio group that drives the product form's own quantity input. Buy 1 keeps
 * Horizon's native add to cart; Buy 2 and Buy 3 expose a size/colour per top, and
 * product-form.js asks getCartItems() for those variants when Add to Cart is pressed. The discounts
 * are applied by Shopify automatic discounts; the prices here are only derived from the selected
 * variant's real price and the tier discounts configured in the snippet.
 *
 * snippets/offer-tiers.liquid reuses this element with data-pricing="tiers": offer cards whose prices come
 * from each tier's own rows and its explicit discount (assets/offer-pricing.js), a colour thumbnail per unit
 * row, and data-sync-main (no separate variant picker; the form's variant follows the selected tier's rows).
 * Its rows start on data-default-variant (first available size in wearing order, or the URL's variant).
 * data-mode="single" is the same element with one always-visible row and no cards, for products without
 * offers: the native quantity selector stays and Add to Cart adds the row's variant x quantity.
 *
 * Tiers are keyed by position (data-tier-index), so a set and a "Buy 3" tier can share a quantity. A set's
 * rows carry data-preset ({"<option index>": "<value>"}): those options are fixed (disabled selects) and only
 * the remaining options follow the shopper's choice.
 *
 * getState() is the one source of truth for what is selected (tier, rows' variants, quantities, pricing).
 * Whenever it changes a bubbling `bundle-offers:selection` event carries it (the sticky Add to Cart bar uses it).
 */
class BundleOffers extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Record<string, number>} */
  #variantPrices = {};

  /** @type {Array<{id: string, options: string[], available: boolean, price?: number, compare?: number, price_text?: string, compare_text?: string | null, image?: string | null, media?: string | null}>} */
  #variants = [];

  /** @type {string} */
  #lastSelection = '';

  /** Position of the selected offer card, or -1 before one is chosen. */
  #selectedIndex = -1;

  connectedCallback() {
    this.form = this.closest('product-form-component');
    this.quantityInput = /** @type {HTMLInputElement | null} */ (
      this.form?.querySelector('quantity-selector-component input[name="quantity"]') ?? null
    );
    if (!this.form || !this.quantityInput) {
      this.hidden = true;
      return;
    }

    this.options = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[role="radio"]')));

    try {
      this.#variantPrices = JSON.parse(this.querySelector('[data-variant-prices]')?.textContent || '{}');
    } catch {
      this.#variantPrices = {};
    }
    try {
      this.#variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.options.forEach((option, index) => {
      option.addEventListener('click', () => this.selectTier(index), { signal });
    });
    this.querySelector('[role="radiogroup"]')?.addEventListener('keydown', this.#onKeydown, { signal });

    for (const top of this.querySelectorAll('[data-bundle-top]')) {
      top.addEventListener('change', (event) => this.#onTopChange(/** @type {HTMLElement} */ (top), event), {
        signal,
      });
    }
    this.#resetTops(this.#variants.find((variant) => variant.id === this.dataset.defaultVariant));
    if (this.#isSingle) {
      // One plain row: it is always shown and the quantity selector stays the shopper's.
      this.quantityInput.addEventListener('input', () => this.#updateCustomState(), { signal });
      document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
      this.#updateCustomState();
      return;
    }

    // Keep the cards in sync with the native +/- buttons and typed quantities.
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    this.quantityInput.addEventListener('input', () => this.#syncSelection(), { signal });

    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal,
    });

    // Preselect the default tier unless the shopper has already changed the quantity.
    const min = Number(this.quantityInput.min) || 1;
    if (Number(this.quantityInput.value) === min) {
      const defaultIndex = this.dataset.defaultTier ? Number(this.dataset.defaultTier) : -1;
      if (defaultIndex >= 0 && this.options[defaultIndex]) this.selectTier(defaultIndex);
      else this.select(Number(this.dataset.defaultQuantity) || min);
    } else {
      this.#syncSelection();
    }
    this.#updatePrices();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  get #isSingle() {
    return this.dataset.mode === 'single';
  }

  /**
   * Selects the offer card at the given position and sets the form quantity to its unit count.
   * @param {number} index
   */
  selectTier(index) {
    const option = this.options?.[index];
    if (!option) return;
    this.#selectedIndex = index;
    this.select(Number(option.dataset.quantity));
  }

  /**
   * Sets the product form quantity to the given tier.
   * @param {number} quantity
   */
  select(quantity) {
    const { quantityInput } = this;
    if (!quantityInput || !quantity) return;
    // Keep the chosen card when it has this quantity; otherwise the first card with it.
    const current = this.options?.[this.#selectedIndex];
    if (!current || Number(current.dataset.quantity) !== quantity) {
      this.#selectedIndex = this.options?.findIndex((option) => Number(option.dataset.quantity) === quantity) ?? -1;
    }

    quantityInput.value = String(quantity);

    const selector = /** @type {any} */ (quantityInput.closest('quantity-selector-component'));
    selector?.updateButtonStates?.();
    // Broadcast through the quantity selector so the sticky Add to Cart bar picks it up too.
    selector?.onQuantityChange?.();

    this.#syncSelection();
  }

  #syncSelection() {
    const quantity = Number(this.quantityInput?.value) || 0;
    const current = this.options?.[this.#selectedIndex];
    if (!current || Number(current.dataset.quantity) !== quantity) {
      this.#selectedIndex = this.options?.findIndex((option) => Number(option.dataset.quantity) === quantity) ?? -1;
    }
    const selectedIndex = this.#selectedIndex;

    (this.options ?? []).forEach((option, index) => {
      const selected = index === selectedIndex;
      option.setAttribute('aria-checked', String(selected));
      option.tabIndex = selected || (selectedIndex < 0 && index === 0) ? 0 : -1;
    });

    if (!this.#isSingle) {
      const selectedCard = this.options?.[selectedIndex];
      for (const panel of this.querySelectorAll('[data-bundle-variants]')) {
        const element = /** @type {HTMLElement} */ (panel);
        element.hidden = !selectedCard || element.closest('.offer-tiers__tier') !== selectedCard.closest('.offer-tiers__tier');
      }
    }
    this.#updateCustomState();
  }

  /**
   * Variants to add for the selected Buy 2 / Buy 3 tier, merged per variant.
   * Returns null when no per-top tier is selected, so the native single-variant add runs instead,
   * and an empty array when a chosen combination can't be bought (the error is shown here).
   * @returns {Array<{variantId: string, quantity: number}> | null}
   */
  getCartItems() {
    const panel = this.#activePanel();
    if (!panel) return null;

    /** @type {Map<string, number>} */
    const quantities = new Map();
    for (const top of panel.querySelectorAll('[data-bundle-top]')) {
      const variant = this.#variantForTop(/** @type {HTMLElement} */ (top));
      if (!variant?.available) {
        this.#showError(/** @type {HTMLElement} */ (top));
        return [];
      }
      quantities.set(variant.id, (quantities.get(variant.id) ?? 0) + 1);
    }

    // Single mode: the one row's variant, times the quantity selector.
    const multiplier = this.#isSingle ? Math.max(1, Number(this.quantityInput?.value) || 1) : 1;
    return Array.from(quantities, ([variantId, quantity]) => ({ variantId, quantity: quantity * multiplier }));
  }

  /** @returns {HTMLElement | null} */
  #activePanel() {
    return this.querySelector('[data-bundle-variants]:not([hidden])');
  }

  /** @returns {{id: string, options: string[], available: boolean} | undefined} */
  #mainVariant() {
    const id = /** @type {HTMLInputElement | null} */ (this.form?.querySelector('input[name="id"]'))?.value;
    return this.#variants.find((variant) => variant.id === id);
  }

  /** @param {HTMLElement} top */
  #selectsFor(top) {
    return /** @type {HTMLSelectElement[]} */ (Array.from(top.querySelectorAll('select[data-option-index]')));
  }

  /** @param {HTMLElement} top */
  #variantForTop(top) {
    const selects = this.#selectsFor(top);
    return this.#variants.find((variant) =>
      selects.every((select) => variant.options[Number(select.dataset.optionIndex)] === select.value)
    );
  }

  /**
   * Points every top the shopper hasn't changed at the given variant (default: the main picker's).
   * @param {{id: string, options: string[], available: boolean} | undefined} [main]
   */
  #resetTops(main = this.#mainVariant()) {
    for (const top of this.querySelectorAll('[data-bundle-top]')) {
      const element = /** @type {HTMLElement} */ (top);
      const preset = this.#presetFor(element);
      if (main && element.dataset.touched !== 'true') {
        for (const select of this.#selectsFor(element)) {
          if (Object.hasOwn(preset, select.dataset.optionIndex ?? '')) continue;
          select.value = main.options[Number(select.dataset.optionIndex)] ?? select.value;
        }
      }
      for (const select of this.#selectsFor(element)) {
        const value = preset[select.dataset.optionIndex ?? ''];
        if (value === undefined) continue;
        select.value = value;
        select.disabled = true;
      }
      this.#updateAvailability(element);
    }
    this.#updateCustomState();
  }

  /**
   * A set row's fixed options, as {"<option index>": "<value>"}.
   * @param {HTMLElement} top
   * @returns {Record<string, string>}
   */
  #presetFor(top) {
    if (!top.dataset.preset) return {};
    try {
      return JSON.parse(top.dataset.preset);
    } catch {
      return {};
    }
  }

  /**
   * @param {HTMLElement} top
   * @param {Event} event
   */
  #onTopChange(top, event) {
    top.dataset.touched = 'true';
    top.classList.remove('bundle-offer__top--error');

    // If the new value has no buyable match, move the other option to the first value that does.
    const changed = /** @type {HTMLSelectElement} */ (event.target);
    if (!this.#variantForTop(top)?.available) {
      const others = this.#selectsFor(top).filter((select) => select !== changed && !select.disabled);
      for (const other of others) {
        const fallback = Array.from(other.options).find((option) => {
          const previous = other.value;
          other.value = option.value;
          const ok = this.#variantForTop(top)?.available;
          other.value = previous;
          return ok;
        });
        if (fallback) other.value = fallback.value;
      }
    }

    this.#updateAvailability(top);
    this.#updateCustomState();
    this.#syncGallery(top, changed);
  }

  /**
   * With data-sync-gallery, shows the chosen colour's image in the product gallery: the row's variant's
   * featured media, else another variant of the same colour that has one. Size-only changes leave the
   * gallery alone (unless the product has no colour option), and nothing moves when no image is linked.
   * The gallery's slides carry their media id as slide-id (snippets/product-media-gallery-content.liquid).
   * @param {HTMLElement} top
   * @param {HTMLSelectElement} changed
   */
  #syncGallery(top, changed) {
    if (this.dataset.syncGallery !== 'true') return;

    const thumb = /** @type {HTMLElement | null} */ (top.querySelector('[data-top-thumb]'));
    const colorIndex = thumb ? Number(thumb.dataset.optionIndex) : null;
    if (colorIndex !== null && Number(changed.dataset.optionIndex) !== colorIndex) return;

    const variant = this.#variantForTop(top);
    if (!variant) return;
    const mediaId =
      variant.media ??
      (colorIndex === null
        ? null
        : this.#variants.find((entry) => entry.options[colorIndex] === variant.options[colorIndex] && entry.media)?.media);
    if (!mediaId) return;

    const gallery = this.closest('.shopify-section')?.querySelector('media-gallery');
    const slideshow = /** @type {any} */ (gallery?.querySelector('slideshow-component'));
    if (slideshow?.select && gallery?.querySelector(`slideshow-slide[slide-id="${mediaId}"]`)) {
      slideshow.select({ id: mediaId });
      return;
    }
    // Grid layout: bring the matching image into view instead.
    const item = gallery?.querySelector(`[data-media-id="${mediaId}"]`);
    if (item instanceof HTMLElement) {
      item.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
      });
    }
  }

  /**
   * Labels values that are sold out with the top's other current choices.
   * @param {HTMLElement} top
   */
  #updateAvailability(top) {
    const selects = this.#selectsFor(top);
    for (const select of selects) {
      const index = Number(select.dataset.optionIndex);
      for (const option of select.options) {
        const available = this.#variants.some(
          (variant) =>
            variant.available &&
            variant.options[index] === option.value &&
            selects.every(
              (other) => other === select || variant.options[Number(other.dataset.optionIndex)] === other.value
            )
        );
        option.textContent = available ? option.value : `${option.value} – ${this.dataset.soldOutLabel || 'Sold out'}`;
      }
    }
    this.#updateThumb(top);
  }

  /**
   * Shows the chosen colour's product image next to its dropdown, when the row has a thumbnail.
   * @param {HTMLElement} top
   */
  #updateThumb(top) {
    const thumb = /** @type {HTMLImageElement | null} */ (top.querySelector('[data-top-thumb]'));
    if (!thumb) return;

    const index = Number(thumb.dataset.optionIndex);
    const value = this.#selectsFor(top).find((select) => Number(select.dataset.optionIndex) === index)?.value;
    const image =
      this.#variantForTop(top)?.image ??
      this.#variants.find((variant) => variant.options[index] === value && variant.image)?.image;

    thumb.hidden = !image;
    if (image && thumb.getAttribute('src') !== image) thumb.src = image;
  }

  /**
   * Flags the form while the selected tier holds variants other than the main selection, so the
   * wallet buttons (which can only buy the main variant) are hidden by the snippet's CSS.
   */
  #updateCustomState() {
    const panel = this.#activePanel();
    if (panel && this.dataset.syncMain === 'true') this.#syncMainVariant(panel);
    const mainId = this.#mainVariant()?.id;
    const custom =
      !!panel &&
      Array.from(panel.querySelectorAll('[data-bundle-top]')).some(
        (top) => this.#variantForTop(/** @type {HTMLElement} */ (top))?.id !== mainId
      );
    this.form?.toggleAttribute('data-bundle-custom', custom);
    this.#announceSelection();
  }

  /**
   * The selected tier's rows grouped per variant in row order (kept for callers that only need the items).
   * @returns {{ items: Array<{id: string, title: string, image: string | null, quantity: number}> } | null}
   */
  getSelection() {
    const state = this.getState();
    return state ? { items: state.items } : null;
  }

  /**
   * The one source of truth for the purchase being configured: mode, tier, the rows' real variants
   * (grouped, with quantities) and the tier's price from assets/offer-pricing.js. Null when no rows are shown.
   */
  getState() {
    const panel = this.#activePanel();
    if (!panel) return null;

    const variants = this.#panelVariants(panel);
    /** @type {Map<string, {id: string, title: string, options: string[], image: string | null, price: number, quantity: number}>} */
    const items = new Map();
    for (const variant of variants) {
      const item = items.get(variant.id);
      if (item) item.quantity += 1;
      else
        items.set(variant.id, {
          id: variant.id,
          title: variant.options.join(' / '),
          options: variant.options,
          image: this.#imageFor(variant),
          price: variant.price ?? 0,
          quantity: 1,
        });
    }

    const multiplier = this.#isSingle ? Math.max(1, Number(this.quantityInput?.value) || 1) : 1;
    if (multiplier > 1) for (const item of items.values()) item.quantity *= multiplier;

    const card = this.#isSingle ? null : this.#cardFor(panel);
    const discount = card ? this.#discountFor(card) : normalizeDiscount({});
    const units = variants.flatMap((variant) => Array.from({ length: multiplier }, () => variant));
    return {
      mode: this.dataset.mode || 'tiers',
      quantity: units.length,
      tier: card ? { quantity: Number(card.dataset.quantity) || units.length, discount } : null,
      items: Array.from(items.values()),
      pricing: priceTier(units.map((variant) => ({ price: variant.price ?? 0, compare: variant.compare ?? 0 })), discount),
    };
  }

  /**
   * @param {HTMLElement} panel
   * @returns {Array<{id: string, options: string[], available: boolean, price?: number, compare?: number, image?: string | null}>}
   */
  #panelVariants(panel) {
    return Array.from(panel.querySelectorAll('[data-bundle-top]'))
      .map((top) => this.#variantForTop(/** @type {HTMLElement} */ (top)))
      .filter((variant) => !!variant);
  }

  /**
   * The offer card that owns a tier panel.
   * @param {Element} panel
   * @returns {HTMLElement | null}
   */
  #cardFor(panel) {
    return /** @type {HTMLElement | null} */ (panel.closest('.offer-tiers__tier')?.querySelector('[role="radio"]') ?? null);
  }

  /** @param {HTMLElement} card */
  #discountFor(card) {
    const { discountType, discountValue, discountUnits } = card.dataset;
    if (!discountType) return normalizeDiscount({ free: card.dataset.free });
    return normalizeDiscount({ discount: { type: discountType, value: discountValue, units: discountUnits } });
  }

  /**
   * The variant's own image, else an image of another variant in the same colour (as the row thumbnails do).
   * @param {{options: string[], image?: string | null}} variant
   * @returns {string | null}
   */
  #imageFor(variant) {
    if (variant.image) return variant.image;
    const thumb = /** @type {HTMLElement | null} */ (this.querySelector('[data-top-thumb]'));
    if (!thumb) return null;
    const index = Number(thumb.dataset.optionIndex);
    return this.#variants.find((entry) => entry.options[index] === variant.options[index] && entry.image)?.image ?? null;
  }

  #announceSelection() {
    if (this.dataset.pricing === 'tiers') this.#updateTierPrices();
    const state = this.getState();
    if (!state) return;

    const signature = `${this.#selectedIndex}|${state.tier?.quantity ?? 0}|${state.items.map((item) => `${item.id}x${item.quantity}`).join(',')}`;
    if (signature === this.#lastSelection) return;
    this.#lastSelection = signature;

    this.dispatchEvent(new CustomEvent('bundle-offers:selection', { bubbles: true, detail: state }));
  }

  /**
   * Offer tiers have no separate variant picker: when every row of the selected tier holds the same
   * variant, point the form's variant id at it so Buy Now (accelerated checkout) buys that variant.
   * @param {HTMLElement} panel
   */
  #syncMainVariant(panel) {
    const ids = new Set(
      Array.from(panel.querySelectorAll('[data-bundle-top]'), (top) => this.#variantForTop(/** @type {HTMLElement} */ (top))?.id)
    );
    const [id] = ids;
    const input = /** @type {HTMLInputElement | null} */ (this.form?.querySelector('input[name="id"]'));
    if (ids.size !== 1 || !id || !input || input.value === id) return;

    input.value = id;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @param {HTMLElement} top */
  #showError(top) {
    top.classList.add('bundle-offer__top--error');
    this.#selectsFor(top)[0]?.focus();
  }

  /** Recalculates the offer prices (offer tiers: from each tier's rows; legacy bundle: from the main variant). */
  #updatePrices() {
    if (this.dataset.pricing === 'tiers') {
      this.#updateTierPrices();
      return;
    }

    const variantId = /** @type {HTMLInputElement | null} */ (this.form?.querySelector('input[name="id"]'))?.value;
    const unitPrice = variantId ? this.#variantPrices[variantId] : undefined;
    if (unitPrice === undefined) return;

    const tier2Percent = Number(this.dataset.tier2Percent) || 0;
    const tier3AmountOff = Number(this.dataset.tier3AmountOff) || 0;

    // Shopify discounts each unit by its percentage rounded down to the cent; mirror that exactly.
    /** @param {number} percent */
    const unitSavings = (percent) => Math.floor((unitPrice * percent) / 100);

    /** @type {Record<number, {regular: number, savings: number}>} */
    const tiers = {
      1: { regular: unitPrice, savings: 0 },
      2: { regular: unitPrice * 2, savings: unitSavings(tier2Percent) },
      // Legacy bundle markup only: Buy 3 is a fixed amount off the order, not a percentage.
      3: { regular: unitPrice * 3, savings: Math.min(tier3AmountOff, unitPrice * 3) },
    };

    for (const option of this.options ?? []) {
      const tier = tiers[Number(option.dataset.quantity)];
      if (!tier) continue;

      this.#setMoney(option.querySelector('[data-tier-total]'), tier.regular - tier.savings);
      this.#setMoney(option.querySelector('[data-tier-regular]'), tier.regular);
      this.#setMoney(option.querySelector('[data-tier-savings]'), tier.savings);
    }
  }

  /**
   * Offer cards: each tier is priced from its own rows' variants and its explicit discount
   * (assets/offer-pricing.js), so mixed colours/sizes and per-variant prices stay exact. The crossed-out
   * price is the units' compare-at (or regular) prices and is hidden unless it is higher.
   * Single mode has no cards; it shows the row's variant price in the product's price blocks instead.
   */
  #updateTierPrices() {
    if (this.#isSingle) {
      this.#updateMainPrice();
      return;
    }

    for (const option of this.options ?? []) {
      const panel = option.closest('.offer-tiers__tier')?.querySelector('[data-bundle-variants]');
      if (!(panel instanceof HTMLElement)) continue;

      const variants = this.#panelVariants(panel);
      if (!variants.length) continue;
      const price = priceTier(
        variants.map((variant) => ({ price: variant.price ?? 0, compare: variant.compare ?? 0 })),
        this.#discountFor(option)
      );

      this.#setMoney(option.querySelector('[data-tier-total]'), price.total);
      this.#setMoney(option.querySelector('[data-tier-regular]'), price.regular);
      this.#setMoney(option.querySelector('[data-tier-unit]'), price.perUnit);

      const compare = /** @type {HTMLElement | null} */ (option.querySelector('[data-tier-compare]'));
      if (compare) compare.hidden = price.regular <= price.total;
    }
  }

  /**
   * Single mode: the price blocks (product details and sticky bar) show the row's variant, using the
   * price text Liquid formatted for that variant. Only the text changes; variants of one product that
   * differ in having a compare-at price keep the first render's layout (documented limitation).
   */
  #updateMainPrice() {
    const panel = this.#activePanel();
    const variant = panel ? this.#panelVariants(panel)[0] : undefined;
    if (!variant?.price_text) return;

    const section = this.closest('.shopify-section');
    const containers = section?.querySelectorAll(
      '.product-details [ref="priceContainer"], .sticky-add-to-cart__price [ref="priceContainer"]'
    );
    for (const container of containers ?? []) {
      const price = container.querySelector('.price bdi');
      if (price && price.textContent !== variant.price_text) price.textContent = variant.price_text;
      const compare = container.querySelector('.compare-at-price bdi');
      if (compare && variant.compare_text && compare.textContent !== variant.compare_text) {
        compare.textContent = variant.compare_text;
      }
    }
  }

  /**
   * @param {Element | null} element
   * @param {number} cents
   */
  #setMoney(element, cents) {
    if (!element) return;
    element.textContent = formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }

  /** @param {Event} event */
  #onQuantityUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.form?.contains(event.target)) return;
    this.#syncSelection();
  };

  /** @param {Event} event */
  #onProductSelect = (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (!(promise instanceof Promise)) return;

    // product-form-component updates the hidden variant id once the section fetch resolves.
    promise
      .then(() =>
        requestAnimationFrame(() => {
          this.#updatePrices();
          this.#resetTops();
        })
      )
      .catch(() => {});
  };

  /** Arrow-key navigation for the radio group (Space/Enter activate the focused button natively). */
  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key) || !this.options?.length) return;
    // Leave arrow keys alone inside the per-top size/colour dropdowns.
    if (!this.options.includes(/** @type {HTMLButtonElement} */ (event.target))) return;

    event.preventDefault();
    const current = this.options.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
    const last = this.options.length - 1;
    let next = current;

    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = current >= last ? 0 : current + 1;
    else next = current <= 0 ? last : current - 1;

    const target = this.options[next];
    if (!target) return;
    target.focus();
    this.selectTier(next);
  };
}

if (!customElements.get('bundle-offers')) {
  customElements.define('bundle-offers', BundleOffers);
}
